import { useMemo } from 'react';

import { IAnyModelType, Instance, SnapshotIn } from 'mobx-state-tree';

import useModel, { UseModelOptions } from '../useModel';
import useFilters from './useFilters';
import useLimit from './useLimit';
import usePage from './usePage';
import useSearch from './useSearch';

interface UseTableModelOptions extends UseModelOptions {
	filterKeys?: string[];
	arrayFilterKeys?: string[];
}

/**
 * Custom hook for managing table model state with pagination, search, and filtering capabilities.
 *
 * @template T - Type extending `IAnyModelType` for the MobX-State-Tree model.
 * @param {T} Model - The MobX-State-Tree model to use.
 * @param {SnapshotIn<T>} [initial={}] - Initial state for the model.
 * @param {any[]} [deps=[]] - Dependencies array for memoization.
 * @param {UseTableModelOptions} [options={}] - Configuration options for the table model.
 * @returns {Instance<T>} - The MobX-State-Tree model instance with extended state.
 */
export default function useTableModel<T extends IAnyModelType>(
	Model: T,
	initial: SnapshotIn<T> = {} as SnapshotIn<T>,
	deps: any[] = [],
	options: UseTableModelOptions = {},
): Instance<T> {
	const { filterKeys, arrayFilterKeys, ...modelOptions } = options;

	const filters = useFilters({ filterKeys, arrayFilterKeys });
	const limit = useLimit(Model.properties.limit?._defaultValue);
	const page = usePage();
	const search = useSearch();

	const hasFilters = Object.keys(filters).length > 0;

	const extendedInitial = useMemo(
		() => ({
			page,
			limit,
			search,
			...initial,
			...(hasFilters && { filters }),
		}),
		[initial, page, limit, search, hasFilters, filters],
	);

	return useModel(Model, extendedInitial, deps, modelOptions);
}
